"use client";

import {
  AlertTriangle,
  CheckCircle,
  FileText,
  Sparkles,
  Shield,
  Car,
  Building2,
  Umbrella,
  Maximize2,
  CircleHelp,
  CircleAlert,
  Circle
} from "lucide-react";
import { Card } from "../ui/card";
import { Badge } from "../ui/badge";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../config/config";
import { lobMap } from "../../config/constant";

const statusMap = {
  "under-review": {
    text: "Under Review",
    class: "text-white-700 bg-orange-100 text-orange-700",
    color:
      "from-orange-200 to-orange-50 rounded-lg border border-orange-500 p-4"
  },
  quoted: {
    text: "Quoted",
    class: "text-white-700 bg-white-100",
    color: "bg-white-100 border-orange-500 text-white-700"
    //icon: <CheckCircle className="h-3.5 w-3.5" />
  },
  success: {
    text: "Success",
    class: "bg-white-100 text-white-700",
    color: "bg-white-100 border-orange-500 text-white-700"
    // icon: <CheckCircle className="h-3.5 w-3.5" />
  },
  rejected: {
    text: "Rejected",
    class: "bg-gray-100 text-gray-700",
    color: "bg-gray-100 border-gray-200 text-gray-700"
    // icon: <X className="h-3.5 w-3.5" />
  },
  error: {
    text: "Error",
    class: "bg-red-100 text-red-700",
    color: "bg-red-100 border-red-500 text-red-700",
    icon: <AlertTriangle className="h-3.5 w-3.5" />
  }
};

const doNotWriteStatus = {
  green: {
    text: "Under Review",
    class: "text-white-700 bg-green-100 text-green-700",
    color: "from-green-200 to-green-50 rounded-lg border border-green-100 p-4",
    icon: <CheckCircle className="h-3.5 w-3.5 text-green-700" />
  },
  yellow: {
    text: "Rejected",
    class: "text-white-700 bg-orange-100 text-orange-700",
    color:
      "from-orange-200 to-orange-50 rounded-lg border border-orange-100 p-4",
    icon: <AlertTriangle className="h-3.5 w-3.5 text-orange-700" />
  },
  red: {
    text: "Approved",
    class: "text-red-700 bg-red-100 text-red-700",
    color: "from-red-200 to-red-50 rounded-lg border border-red-100 p-4"
  }
};

const doNotWriteStatusDisplay = {
  "under-review": "Under Review",
  approved: "Approved",
  rejected: "Rejected"
};

export function DecisionMatrixCardV2 ({ onCommentsGuidanceChange }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const selectedAppId = useSelector(state => state.intakeData.selectedAppId);
  const [decisionMatrix, setDecisionMatrix] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      const accessToken = localStorage.getItem("access_token");
      const refreshToken = localStorage.getItem("refresh_token");

      if (!accessToken || !refreshToken) {
        navigate("/");
        return;
      }

      try {
        const decisionMatrix = await fetch(
          `${API_BASE_URL}/insureai/ml/intake/applications/v2/decision_matrix/?application_id=${selectedAppId}`,
          {
            method: "GET",
            headers: {
              "X-Access-Token": accessToken,
              "X-Refresh-Token": refreshToken
            }
          }
        );
        const data = await decisionMatrix.json();
        setDecisionMatrix(data);
        // Pass CommentsGuidance up to parent
        if (data?.CommentsGuidance) {
          onCommentsGuidanceChange(data.CommentsGuidance);
        }
        if (!decisionMatrix.ok) {
          throw new Error("Failed to fetch decision matrix");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedAppId]);

  useEffect(() => {
    const loadGuidanceData = async () => {
      try {
        setLoading(true);
        // Removed problematic and unused data fetching logic
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadGuidanceData();
  }, []);

  if (loading) {
    return <div className="p-4">Loading guidance data...</div>;
  }

  if (error) {
    return (
      <div className="p-4 text-red-600">
        Error loading guidance data: {error}
      </div>
    );
  }

  // Function to handle maximize button click and open new tab
  const handleMaximizeClick = (e, lobName) => {
    const url = `/app/lob-details/${lobName}/${selectedAppId}`;
    window.open(url, "_blank");
  };

  return (
    <div>
      <Card className="p-5 bg-white shadow-sm relative">
        {/* Decision Matrix Header */}
        <div className="flex justify-between items-start mb-6">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold flex items-center">
              <FileText className="h-4 w-4 mr-2 text-[#FF5533]" />
              Decision Matrix
            </h3>
            <Badge
              variant="outline"
              className="bg-blue-50 text-blue-600 border-blue-200 text-xs flex items-center gap-1"
            >
              <Sparkles className="h-3 w-3" />
              AI-Generated Data
            </Badge>
          </div>
        </div>

        {/* Main Decision Grid Layout */}
        <div className="grid grid-cols-12 gap-6">
          {/* Left side: Decision Grid (9 columns) */}
          <div className="col-span-12">
            {/* Appetite Check Section */}
            <div
              className={`bg-gradient-to-r mb-4 shadow-sm ${
                doNotWriteStatus[decisionMatrix?.DoNotWrite?.color]?.color
              }`}
            >
              <div className="flex items-start">
                <div className="rounded-full bg-white p-2 mr-1">
                  {doNotWriteStatus[decisionMatrix?.DoNotWrite?.color]
                    ?.icon || <CircleAlert className="h-5 w-5 text-red-600" />}
                </div>
                <div className="flex-1">
                  <div className="flex items-center mb-2">
                    <h3 className={`text-base font-medium`}>Do Not Write</h3>
                    <Badge
                      className={`ml-auto ${
                        doNotWriteStatus[decisionMatrix?.DoNotWrite?.color]
                          ?.class
                      }`}
                    >
                      {doNotWriteStatusDisplay[
                        decisionMatrix?.DoNotWrite?.status
                      ] || decisionMatrix?.DoNotWrite?.status}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-700">
                    {typeof decisionMatrix?.DoNotWrite?.summary === "string" &&
                    decisionMatrix?.DoNotWrite?.summary.includes("<") ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: decisionMatrix?.DoNotWrite?.summary
                        }}
                      />
                    ) : (
                      decisionMatrix?.DoNotWrite?.summary || "Unknown"
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* LOB Folders Grid */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              {(decisionMatrix?.LobMatrix || [{}]).map((lob, idx) => {
                // Map status to badge and color

                const statusBadge =
                  statusMap[lob.status?.toLowerCase()] ||
                  statusMap["under-review"];

                // Icon mapping for LOBs
                const lobIcons = {
                  general_liability: <Shield className="h-4 w-4" />,
                  property: <Building2 className="h-4 w-4" />,
                  commercial_auto: <Car className="h-4 w-4" />,
                  business_auto: <Car className="h-4 w-4" />,
                  workers_compensation: <Umbrella className="h-4 w-4" />,
                  crime: <AlertTriangle className="h-4 w-4" />
                };
                const lobIcon = lobIcons[lob.LobName] || (
                  <Shield className="h-4 w-4" />
                );

                return (
                  <Card
                    key={lob.LobName + idx}
                    className={`p-4 cursor-pointer transition-all duration-200 hover:shadow-md relative ${statusBadge.color}`}
                    onClick={e => handleMaximizeClick(e, lob.LobName)}
                  >
                    {/* Maximize button */}
                    <div
                      className="absolute bottom-2 right-2 h-6 w-6 p-2 bg-white rounded-full shadow-sm cursor-pointer hover:bg-gray-100 transition-colors flex items-center justify-center"
                      onClick={e => {
                        e.stopPropagation(); // Prevent card click
                        handleMaximizeClick(e, lob.LobName);
                      }}
                      aria-label="Maximize"
                    >
                      <Maximize2 className="h-3.5 w-3.5 text-gray-600" />
                    </div>

                    <div className="flex items-center justify-between mb-3">
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          {lobIcon}
                          <h4 className="text-sm font-medium">
                            {lobMap[lob.LobName] || lob.LobName}
                          </h4>
                          <CircleHelp className="h-3.5 w-3.5 text-gray-500 pl-10" />
                          <div className="flex items-center gap-0.5">
                            <h4 className="text-sm font-medium">
                              Score: &nbsp;
                            </h4>
                            {[...Array(10)].map((_, i) => (
                              <Circle
                                key={i}
                                className={`h-3 w-3 ${
                                  i < (lob.score || 0)
                                    ? "text-orange-500 fill-orange-400"
                                    : "text-gray-300"
                                }`}
                                fill={
                                  i < (lob.score || 0) ? "currentColor" : "none"
                                }
                                strokeWidth={2}
                              />
                            ))}
                            <h4 className="ml-2 text-xs text-black-500">
                              {lob.score ?? 0}/10
                            </h4>
                          </div>
                        </div>
                      </div>
                      {/* <div className="flex items-center">
                        <Badge
                          className={`text-xs ${statusBadge.class} border-0`}
                        >
                          {statusBadge.text}
                        </Badge>
                        <div
                          className={`flex items-center justify-center h-5 w-5 rounded-full ${statusBadge.color}`}
                        >
                          {statusBadge.icon}
                        </div>
                      </div> */}
                    </div>

                    {/* Info tabs */}
                    <div className="flex gap-1 mb-3">
                      {Array.isArray(lob.infos) && lob.infos.length > 0 ? (
                        lob.infos.map((info, i) => (
                          <div key={i} className="flex gap-1">
                            <Badge
                              variant="outline"
                              className="text-xs bg-white/50 border-orange-200 text-orange-700"
                            >
                              {info.name}
                            </Badge>
                          </div>
                        ))
                      ) : (
                        <Badge
                          variant="outline"
                          className="text-xs bg-white/50"
                        >
                          {lob.info?.[0]?.info1 || "Info-1"}
                        </Badge>
                      )}
                    </div>

                    {/* Enhanced text summary */}
                    <div className="text-sm space-y-2">
                      <p className="opacity-90">
                        <span className="font-medium">Coverage Summary:</span>{" "}
                        {typeof lob.summary === "string" &&
                        lob.summary.includes("<") ? (
                          <span
                            dangerouslySetInnerHTML={{ __html: lob.summary }}
                          />
                        ) : (
                          lob.summary || "N/A"
                        )}
                      </p>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Right side: Guidance Tabs (3 columns) */}
          {/* <div className='col-span-3'>
            <Tabs
              value={activeGuidanceTab}
              onValueChange={setActiveGuidanceTab}
              className='w-full'
            >
              <TabsList className='grid w-full grid-cols-2 border background-gray-200 rounded-lg'>
                <TabsTrigger
                  value='comments'
                  className={`${
                    activeGuidanceTab === 'comments'
                      ? 'bg-orange-50 text-orange-700 p-2'
                      : 'bg-white'
                  } p-2 hover:bg-gray-50`}
                >
                  Comments & Guidance
                </TabsTrigger>
                <TabsTrigger
                  value='manual'
                  className={`${
                    activeGuidanceTab === 'manual'
                      ? 'bg-orange-50 text-orange-700 p-2'
                      : 'bg-white'
                  }`}
                >
                  Desk Manual
                </TabsTrigger>
              </TabsList>

              <TabsContent value='comments' className='mt-4'>
                <Card className='p-4'>
                  <h4 className='text-sm font-medium mb-3 text-orange-600'>
                    Comments And Guidance
                  </h4>
                  <div className='space-y-3 text-xs max-h-[400px] overflow-y-auto'>
                    <div
                      className='prose text-xs'
                      dangerouslySetInnerHTML={{
                        __html:
                          decisionMatrix?.CommentsGuidance ||
                          'Data not available'
                      }}
                    />
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value='manual' className='mt-4'>
                <Card className='p-4'>
                  <h4 className='text-sm font-medium mb-3 text-amber-600'>
                    Desk Manual Guidance
                  </h4>
                  <div className='space-y-3 text-xs max-h-[400px] overflow-y-auto'>
                    <div
                      className='prose text-xs'
                      dangerouslySetInnerHTML={{
                        __html:
                          decisionMatrix?.DeskManual || 'Data not available'
                      }}
                    />
                  </div>
                </Card>
              </TabsContent>
            </Tabs>
          </div> */}
        </div>
      </Card>
    </div>
  );
}
